import React, { useEffect, Suspense } from "react";
import RequirePermission from "../components/RequirePermission";
import { Navigate, Route, Routes, Outlet } from "react-router-dom";
// import { useQuery } from "@apollo/client";
// import { GET_USER_PROFILE } from "../api/graphql/graph";
// import axiosBaseConfig from "../api/axios";
import { ToggleProvider } from "../contexts/toggleContext";
import CenteredLoader from "../components/CenteredLoader";
import PrivateRoute from "../components/guards/PrivateRoute";
import PublicRoute from "../components/guards/PublicRoute";
import Logout from "../components/Logout";
import AuthLayout from "../layouts/AuthLayout";
import MainLayout from "../layouts/MainLayout";
import MainCommonLayout from "../layouts/MainCommonLayout";
import { MarketingHeader } from "./Marketing/MarketingHeader";

// Lazy loaded components
const Login = React.lazy(() => import("./Login"));
const Registration = React.lazy(() => import("./Registration"));
const ResetPassword = React.lazy(() => import("./ResetPassword"));
const Dashboard = React.lazy(() => import("./Dashboard"));
const PageNotFound = React.lazy(() => import("./PageNotFound"));
const BalancePage = React.lazy(() => import("./Balance"));
const MyApps = React.lazy(() => import("./MyApps"));
const AddFirstAppModal = React.lazy(() => import("./Apps/AddFirstAppModal"));
const AppOverview = React.lazy(() => import("./AppOverview"));
const AsoReport = React.lazy(() => import("./AsoReport"));
const MyOrders = React.lazy(() => import("./MyOrders"));
const Activity = React.lazy(() => import("./Activity"));
const KeywordPlanner = React.lazy(() => import("./OrderPlanner"));
const NewOrderPage = React.lazy(() => import("./NewOrderPage"));
const NewOrderIndex = React.lazy(() =>
  import("./NewOrderPage").then((module) => ({
    default: module.NewOrderIndex,
  })),
);
const KeywordsOrders = React.lazy(() => import("./KeywordsOrder"));
const RatingsOrder = React.lazy(() => import("./RatingsOrder"));
const ReviewsOrder = React.lazy(() => import("./ReviewsOrder"));
const Settings = React.lazy(() => import("./Settings"));
const HelpPage = React.lazy(() => import("./HelpPage"));
const DirectOrder = React.lazy(() => import("./DirectOrder"));
const AdminLoginToClient = React.lazy(() => import("./AdminLoginToClient"));
const OnboardingWelcome = React.lazy(() => import("./Onboarding/Welcome"));
const OnboardingSteps = React.lazy(() => import("./Onboarding/Steps"));
const ConfirmEmail = React.lazy(() => import("./ConfirmEmail"));
const AcceptInvite = React.lazy(() => import("./AcceptInvite"));
const PrivacyPolicy = React.lazy(() => import("./PrivacyPolicy"));
const TermsOfUse = React.lazy(() => import("./TermsOfUse"));
const UnsubscribeFromMail = React.lazy(() => import("./EmailUnsubscribe"));
const Sandbox = React.lazy(() => import("./Sandbox"));
const DataCenterTour = React.lazy(() => import("./DataCenterTour"));
const Receipt = React.lazy(() => import("./Receipt"));

const Landing = React.lazy(() => import("./Landing"));

const Api = React.lazy(() => import("./Marketing/Api"));
const Partnership = React.lazy(() => import("./Marketing/Partnership"));
const Referral = React.lazy(() => import("./Marketing/Referral"));
const Changelog = React.lazy(() => import("./Marketing/Changelog"));

const MarketingLayoutWrapper: React.FC = () => {
  return (
    <div className="marketing-root">
      <MarketingHeader />
      <Outlet />
    </div>
  );
};

const ProjectRoutes = () => {
  return (
    <ToggleProvider>
      <Suspense fallback={<CenteredLoader />}>
        <Routes>
          <Route element={<MarketingLayoutWrapper />}>
            <Route path="/api" element={<Api />} />
            <Route path="/api.html" element={<Navigate to="/api" replace />} />
            <Route path="/partnership" element={<Partnership />} />
            <Route
              path="/partnership.html"
              element={<Navigate to="/partnership" replace />}
            />
            <Route path="/referral" element={<Referral />} />
            <Route
              path="/referral.html"
              element={<Navigate to="/referral" replace />}
            />
            {/*<Route path="/changelog" element={<Changelog />} />*/}
            <Route
              path="/changelog.html"
              element={<Navigate to="/changelog" replace />}
            />
          </Route>
          <Route path="/sandbox" element={<Sandbox />} />
          <Route path="/onboarding" element={<OnboardingWelcome />} />
          <Route path="/onboarding/steps" element={<OnboardingSteps />} />
          <Route path="/confirm-email" element={<ConfirmEmail />} />
          {/* Public on purpose: the invitation has to be readable before
              signing in, and accepting it asks for the session itself. */}
          <Route path="/invite/:token" element={<AcceptInvite />} />
          {/* Public on purpose: the link comes from a letter and is opened by
              whoever holds it, signed in or not. Behind PrivateRoute it sent
              a signed-out reader to the login form and unsubscribed whoever
              happened to be signed in. The token names the recipient. */}
          <Route
            path="/unsubscribe-from-mail"
            element={<UnsubscribeFromMail />}
          />
          {/* Точка входа из админки: устанавливает сессию, поэтому НЕ должна
              быть за PrivateRoute (иначе guard выкинет на /login до входа). */}
          <Route
            path="/admin-login-to-client"
            element={<AdminLoginToClient />}
          />
          <Route path="/privacy-policy" element={<PrivacyPolicy />} />
          <Route path="/terms-of-use" element={<TermsOfUse />} />

          <Route element={<PublicRoute />}>
            <Route path="/" element={<Landing />} />

            <Route element={<AuthLayout />}>
              <Route path="/login" element={<Login />} />
              <Route path="/registration" element={<Registration />} />
              <Route path="/reset-password" element={<ResetPassword />} />
            </Route>
          </Route>
          <Route element={<PrivateRoute />}>
            <Route element={<MainCommonLayout />}>
              <Route element={<MainLayout />}>
                <Route path="/my-apps" element={<MyApps />} />
                <Route path="/activity" element={<Activity />} />
                <Route path="/settings" element={<Settings />} />
                <Route path="/settings/:tab" element={<Settings />} />
                {/* The old address leads to the same tab: links to it live on in bookmarks. */}
                <Route
                  path="/profile-settings"
                  element={<Navigate to="/settings/profile" replace />}
                />
                <Route path="/help" element={<HelpPage />} />
                <Route path="/FAQ" element={<HelpPage />} />
              </Route>

              <Route element={<MainLayout />}>
                <Route path="*" element={<PageNotFound />} />
                <Route path="/dashboard" element={<Dashboard />} />
                <Route path="/balance" element={<BalancePage />} />
                <Route path="/app-overview" element={<AppOverview />} />
                <Route path="/aso-report" element={<AsoReport />} />
                <Route path={"/orders"} element={<MyOrders />} />
                <Route path={"/keyword-planner"} element={<KeywordPlanner />} />
                {/* New order: the type is picked on the page itself (no modal
                    before it) and its one-page form opens under the cards,
                    inside the app's layout. Placing an order spends the
                    balance: a viewer is stopped at the door. */}
                <Route
                  path={"/new-order"}
                  element={
                    <RequirePermission permission="orders:create">
                      <NewOrderPage />
                    </RequirePermission>
                  }
                >
                  <Route index element={<NewOrderIndex />} />
                  <Route path={"keywords"} element={<KeywordsOrders />} />
                  <Route path={"direct"} element={<DirectOrder />} />
                  <Route path={"ratings"} element={<RatingsOrder />} />
                  <Route path={"reviews"} element={<ReviewsOrder />} />
                </Route>
                {/* The planner shipped as /order-planner first — keep old links alive. */}
                <Route
                  path={"/order-planner"}
                  element={<Navigate to="/keyword-planner" replace />}
                />
              </Route>

              <Route path="/add-app" element={<AddFirstAppModal />} />
              {/* A printable document, no sidebar: the receipt of a crypto
                  top-up (Balance → Top-ups → Receipt). */}
              <Route path="/receipt/:hashId" element={<Receipt />} />
              {/* Full screen, no sidebar: the 3D tour of the account's own
                  apps, keywords and orders. */}
              <Route path="/tour" element={<DataCenterTour />} />

              <Route path="/logout" element={<Logout />} />
            </Route>
          </Route>
        </Routes>
      </Suspense>
    </ToggleProvider>
  );
};

export default ProjectRoutes;
